import { useCallback, useEffect, useState } from 'react'
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Shield,
  UserPlus,
  UserMinus,
  AlertTriangle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import { getApiErrorKey } from '../../utils/apiError'
import type { UserGroup, Right, AdminUser } from '../../types'

// Statuses available per module (only filterable modules are listed)
const MODULE_STATUSES: Record<string, { value: string; labelKey: string }[]> = {
  invoices: [
    { value: 'paid', labelKey: 'adminGroups.statusPaid' },
    { value: 'unpaid', labelKey: 'adminGroups.statusUnpaid' },
    { value: 'draft', labelKey: 'adminGroups.statusDraft' },
    { value: 'cancelled', labelKey: 'adminGroups.statusCancelled' },
  ],
  proposals: [
    { value: 'draft', labelKey: 'adminGroups.statusDraft' },
    { value: 'validated', labelKey: 'adminGroups.statusValidated' },
    { value: 'signed', labelKey: 'adminGroups.statusSigned' },
    { value: 'unsigned', labelKey: 'adminGroups.statusUnsigned' },
    { value: 'cancelled', labelKey: 'adminGroups.statusCancelled' },
  ],
  orders: [
    { value: 'draft', labelKey: 'adminGroups.statusDraft' },
    { value: 'validated', labelKey: 'adminGroups.statusValidated' },
    { value: 'in_progress', labelKey: 'adminGroups.statusInProgress' },
    { value: 'shipped', labelKey: 'adminGroups.statusShipped' },
    { value: 'closed', labelKey: 'adminGroups.statusClosed' },
    { value: 'cancelled', labelKey: 'adminGroups.statusCancelled' },
  ],
  shipments: [
    { value: 'draft', labelKey: 'adminGroups.statusDraft' },
    { value: 'validated', labelKey: 'adminGroups.statusValidated' },
    { value: 'closed', labelKey: 'adminGroups.statusClosed' },
    { value: 'cancelled', labelKey: 'adminGroups.statusCancelled' },
  ],
  projects: [
    { value: 'draft', labelKey: 'adminGroups.statusDraft' },
    { value: 'validated', labelKey: 'adminGroups.statusValidated' },
    { value: 'closed', labelKey: 'adminGroups.statusClosed' },
  ],
}

// Special rights that are not standard module rights (do not grant data access)
const SPECIAL_MODULES = ['all_clients']

type RightConfig = {
  is_enabled: boolean
  allowed_statuses: string[] | null
  access_level: 'read' | 'write'
}

// ── Confirmation modal ──────────────────────────────────────────────────────
function ConfirmModal({
  message,
  onConfirm,
  onCancel,
}: {
  message: string
  onConfirm: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
        <p className="text-gray-800 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            {t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Group form modal (create / edit) ────────────────────────────────────────
function GroupFormModal({
  group,
  onSave,
  onClose,
}: {
  group: Partial<UserGroup> | null
  onSave: (data: Partial<UserGroup>) => Promise<void>
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    name: group?.name ?? '',
    description: group?.description ?? '',
    is_active: group?.is_active ?? true,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError(t('adminGroups.nameRequired'))
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSave(form)
      onClose()
    } catch (err: unknown) {
      setError(getApiErrorKey(err, 'common.error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold">
            {group?.id ? t('adminGroups.editGroup') : t('adminGroups.createGroup')}
          </h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <p className="text-sm text-red-600">{t(error)}</p>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('adminGroups.name')} *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('adminGroups.description')}
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none resize-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              {t('adminGroups.activeGroup')}
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Group detail drawer (members + rights) ──────────────────────────────────
function GroupDetailPanel({
  group,
  allUsers,
  onClose,
  onUpdated,
}: {
  group: UserGroup
  allUsers: AdminUser[]
  onClose: () => void
  onUpdated: () => void
}) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<'members' | 'rights'>('members')
  const [members, setMembers] = useState<AdminUser[]>([])
  // Map rightId → { is_enabled, allowed_statuses }
  const [rightsConfig, setRightsConfig] = useState<Map<number, RightConfig>>(new Map())
  // Full rights list (all available) with is_enabled flag from backend
  const [allRights, setAllRights] = useState<Right[]>([])
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [savingRights, setSavingRights] = useState(false)
  const [addUserId, setAddUserId] = useState('')

  const loadMembers = useCallback(async () => {
    setLoadingMembers(true)
    try {
      const m = await api.getAdminGroupMembers(group.id)
      setMembers(m)
    } finally {
      setLoadingMembers(false)
    }
  }, [group.id])

  const loadRights = useCallback(async () => {
    try {
      const rights = await api.getAdminGroupRights(group.id)
      const safeRights = Array.isArray(rights) ? rights : []
      setAllRights(safeRights)
      const map = new Map<number, RightConfig>()
      safeRights.forEach((r) => {
        map.set(r.id, {
          is_enabled: r.is_enabled ?? false,
          allowed_statuses: r.allowed_statuses ?? null,
          access_level: r.access_level ?? 'read',
        })
      })
      setRightsConfig(map)
    } catch {
      // Rights tab may be unavailable if DB migration has not been applied yet
    }
  }, [group.id])

  useEffect(() => {
    loadMembers()
    loadRights()
  }, [loadMembers, loadRights])

  const addMember = async () => {
    if (!addUserId) return
    await api.addAdminGroupMember(group.id, parseInt(addUserId))
    setAddUserId('')
    await loadMembers()
    onUpdated()
  }

  const removeMember = async (userId: number) => {
    await api.removeAdminGroupMember(group.id, userId)
    await loadMembers()
    onUpdated()
  }

  const toggleRight = (rightId: number) => {
    setRightsConfig((prev) => {
      const next = new Map(prev)
      const cfg = next.get(rightId) ?? {
        is_enabled: false,
        allowed_statuses: null,
        access_level: 'read' as const,
      }
      next.set(rightId, { ...cfg, is_enabled: !cfg.is_enabled })
      return next
    })
  }

  const setAccessLevel = (rightId: number, level: 'read' | 'write') => {
    setRightsConfig((prev) => {
      const next = new Map(prev)
      const cfg = next.get(rightId) ?? {
        is_enabled: true,
        allowed_statuses: null,
        access_level: 'read' as const,
      }
      next.set(rightId, { ...cfg, access_level: level })
      return next
    })
  }

  const setStatusMode = (rightId: number, custom: boolean, moduleName: string) => {
    setRightsConfig((prev) => {
      const next = new Map(prev)
      const cfg: RightConfig = next.get(rightId) ?? {
        is_enabled: true,
        allowed_statuses: null,
        access_level: 'write',
      }
      if (custom) {
        // Select all statuses by default when switching to custom mode
        const allStatuses = (MODULE_STATUSES[moduleName] ?? []).map((s) => s.value)
        next.set(rightId, { ...cfg, allowed_statuses: allStatuses })
      } else {
        next.set(rightId, { ...cfg, allowed_statuses: null })
      }
      return next
    })
  }

  const toggleStatus = (rightId: number, statusValue: string) => {
    setRightsConfig((prev) => {
      const next = new Map(prev)
      const cfg = next.get(rightId)
      if (!cfg) return prev
      const current = cfg.allowed_statuses ?? []
      const newStatuses = current.includes(statusValue)
        ? current.filter((s) => s !== statusValue)
        : [...current, statusValue]
      next.set(rightId, { ...cfg, allowed_statuses: newStatuses })
      return next
    })
  }

  const saveRights = async () => {
    setSavingRights(true)
    try {
      const rightConfigs: Array<{
        right_id: number
        allowed_statuses: string[] | null
        access_level: 'read' | 'write'
      }> = []
      rightsConfig.forEach((cfg, rightId) => {
        if (cfg.is_enabled) {
          rightConfigs.push({
            right_id: rightId,
            allowed_statuses: cfg.allowed_statuses,
            access_level: cfg.access_level,
          })
        }
      })
      await api.updateAdminGroupRights(group.id, rightConfigs)
      onUpdated()
      onClose()
    } finally {
      setSavingRights(false)
    }
  }

  // Count enabled rights for the tab badge
  const enabledCount = Array.from(rightsConfig.values()).filter((c) => c.is_enabled).length

  // Warning: no standard module rights are enabled (special rights like 'all_clients' don't grant data access)
  const hasNoStandardModules =
    allRights.length > 0 &&
    allRights
      .filter((r) => !SPECIAL_MODULES.includes(r.module_name))
      .every((r) => !rightsConfig.get(r.id)?.is_enabled)

  // Users not yet in the group
  const memberIds = new Set(members.map((m) => m.id))
  const availableUsers = allUsers.filter((u) => !memberIds.has(u.id) && u.role !== 'admin')

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="min-w-0 mr-2">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{group.name}</h3>
            {group.description && (
              <p className="text-sm text-gray-500 truncate">{group.description}</p>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-4 sm:px-6">
          <button
            onClick={() => setTab('members')}
            className={`py-3 px-3 sm:px-4 text-sm font-medium border-b-2 -mb-px ${tab === 'members' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Users className="w-4 h-4 inline mr-1" />
            {t('adminGroups.tabMembers')} ({members.length})
          </button>
          <button
            onClick={() => setTab('rights')}
            className={`py-3 px-3 sm:px-4 text-sm font-medium border-b-2 -mb-px ${tab === 'rights' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Shield className="w-4 h-4 inline mr-1" />
            {t('adminGroups.tabRights')} ({enabledCount})
          </button>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {tab === 'members' && (
            <div className="space-y-4">
              {/* Add member */}
              <div className="flex gap-2">
                <select
                  value={addUserId}
                  onChange={(e) => setAddUserId(e.target.value)}
                  className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('adminGroups.selectUser')}</option>
                  {availableUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.lastname} {u.firstname} ({u.email})
                    </option>
                  ))}
                </select>
                <button
                  onClick={addMember}
                  disabled={!addUserId}
                  className="flex items-center gap-1 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-40 text-sm flex-shrink-0"
                >
                  <UserPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('adminGroups.addMember')}</span>
                </button>
              </div>

              {/* Members list */}
              {loadingMembers ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : members.length === 0 ? (
                <p className="text-center text-gray-400 py-8">{t('adminGroups.noMembers')}</p>
              ) : (
                <ul className="space-y-2">
                  {members.map((m) => (
                    <li
                      key={m.id}
                      className="flex items-center justify-between gap-2 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="min-w-0">
                        <span className="font-medium text-gray-900 truncate block">
                          {m.lastname} {m.firstname}
                        </span>
                        <span className="text-xs text-gray-500 truncate block">{m.email}</span>
                      </div>
                      <button
                        onClick={() => removeMember(m.id)}
                        className="p-1 text-red-400 hover:text-red-600 rounded flex-shrink-0"
                        title={t('adminGroups.removeMember')}
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === 'rights' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">{t('adminGroups.rightsHint')}</p>
              <div className="space-y-3">
                {allRights.map((r) => {
                  const cfg = rightsConfig.get(r.id) ?? {
                    is_enabled: false,
                    allowed_statuses: null,
                  }
                  const hasStatuses = !!MODULE_STATUSES[r.module_name]
                  const isCustom = cfg.is_enabled && hasStatuses && cfg.allowed_statuses !== null

                  return (
                    <div
                      key={r.id}
                      className={`rounded-lg border p-3 transition-colors ${cfg.is_enabled ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-white'}`}
                    >
                      {/* Module enable/disable toggle */}
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={cfg.is_enabled}
                          onChange={() => toggleRight(r.id)}
                          className="rounded accent-primary-600"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{r.label}</p>
                          {r.description && (
                            <p className="text-xs text-gray-500">{r.description}</p>
                          )}
                        </div>
                        {cfg.is_enabled && (
                          <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />
                        )}
                      </label>

                      {/* Access level (read / write) — shown when module is enabled */}
                      {cfg.is_enabled && (
                        <div className="mt-2 ml-7 flex flex-wrap gap-4">
                          <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                            <input
                              type="radio"
                              name={`access_level_${r.id}`}
                              checked={cfg.access_level !== 'write'}
                              onChange={() => setAccessLevel(r.id, 'read')}
                              className="accent-primary-600"
                            />
                            {t('adminGroups.accessRead')}
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                            <input
                              type="radio"
                              name={`access_level_${r.id}`}
                              checked={cfg.access_level === 'write'}
                              onChange={() => setAccessLevel(r.id, 'write')}
                              className="accent-primary-600"
                            />
                            {t('adminGroups.accessWrite')}
                          </label>
                        </div>
                      )}

                      {/* Status filter (only for filterable modules) */}
                      {cfg.is_enabled && hasStatuses && (
                        <div className="mt-3 ml-7 space-y-2">
                          <p className="text-xs font-medium text-gray-600">
                            {t('adminGroups.statusFilter')}
                          </p>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                              <input
                                type="radio"
                                name={`status_mode_${r.id}`}
                                checked={cfg.allowed_statuses === null}
                                onChange={() => setStatusMode(r.id, false, r.module_name)}
                                className="accent-primary-600"
                              />
                              {t('adminGroups.allStatuses')}
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                              <input
                                type="radio"
                                name={`status_mode_${r.id}`}
                                checked={cfg.allowed_statuses !== null}
                                onChange={() => setStatusMode(r.id, true, r.module_name)}
                                className="accent-primary-600"
                              />
                              {t('adminGroups.customStatuses')}
                            </label>
                          </div>

                          {isCustom && (
                            <div className="flex flex-wrap gap-2 mt-1">
                              {MODULE_STATUSES[r.module_name].map((s) => {
                                const checked = (cfg.allowed_statuses ?? []).includes(s.value)
                                return (
                                  <button
                                    key={s.value}
                                    type="button"
                                    onClick={() => toggleStatus(r.id, s.value)}
                                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${checked ? 'bg-primary-100 border-primary-400 text-primary-700' : 'bg-white border-gray-300 text-gray-500 hover:border-gray-400'}`}
                                  >
                                    {checked && <Check className="w-3 h-3 inline mr-0.5" />}
                                    {t(s.labelKey)}
                                  </button>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              {hasNoStandardModules && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
                  <span>{t('adminGroups.noModuleWarning')}</span>
                </div>
              )}
              <div className="flex justify-end">
                <button
                  onClick={saveRights}
                  disabled={savingRights}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {savingRights ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function AdminGroups() {
  const { t } = useTranslation()
  const [groups, setGroups] = useState<UserGroup[]>([])
  const [allUsers, setAllUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [formGroup, setFormGroup] = useState<Partial<UserGroup> | null>(null)
  const [detailGroup, setDetailGroup] = useState<UserGroup | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<UserGroup | null>(null)

  const load = async () => {
    try {
      const [g, u] = await Promise.all([api.getAdminGroups(), api.getAdminUsers()])
      setGroups(Array.isArray(g) ? g : [])
      setAllUsers(Array.isArray(u) ? u : [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleSave = async (data: Partial<UserGroup>) => {
    if (formGroup?.id) {
      await api.updateAdminGroup(formGroup.id, data)
    } else {
      await api.createAdminGroup(data)
    }
    await load()
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    await api.deleteAdminGroup(deleteTarget.id)
    setDeleteTarget(null)
    await load()
  }

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('adminGroups.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('adminGroups.subtitle', { count: groups.length })}
          </p>
        </div>
        <button
          onClick={() => setFormGroup({})}
          className="flex items-center self-start gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-4 h-4" />
          {t('adminGroups.createGroup')}
        </button>
      </div>

      {/* Groups table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {groups.length === 0 ? (
          <div className="text-center py-16">
            <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">{t('adminGroups.noGroups')}</p>
          </div>
        ) : (
          <>
            {/* Mobile card layout */}
            <ul className="divide-y divide-gray-100 sm:hidden">
              {groups.map((g) => (
                <li key={g.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <button
                        onClick={() => setDetailGroup(g)}
                        className="font-medium text-primary-600 hover:underline text-left truncate block max-w-full"
                      >
                        {g.name}
                      </button>
                      {g.description && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{g.description}</p>
                      )}
                    </div>
                    <span
                      className={`flex-shrink-0 px-2 py-0.5 text-xs rounded-full font-medium ${g.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                    >
                      {g.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-1 min-w-0">
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500 mr-1">
                        <Users className="w-3.5 h-3.5 text-gray-400" />
                        {g.member_count ?? 0}
                      </span>
                      {(g.rights ?? []).slice(0, 2).map((r) => (
                        <span
                          key={r.id}
                          className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full"
                        >
                          {r.label}
                        </span>
                      ))}
                      {(g.rights?.length ?? 0) > 2 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                          +{(g.rights?.length ?? 0) - 2}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => setDetailGroup(g)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title={t('adminGroups.manageGroup')}
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setFormGroup(g)}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                        title={t('common.edit')}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(g)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-40" />
                  <col className="w-48" />
                  <col className="w-24" />
                  <col className="w-56" />
                  <col className="w-24" />
                  <col className="w-28" />
                </colgroup>
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('adminGroups.name')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('adminGroups.description')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      {t('adminGroups.members')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('adminGroups.rights')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      {t('common.status')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {groups.map((g) => (
                    <tr key={g.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setDetailGroup(g)}
                          className="font-medium text-primary-600 hover:underline text-left w-full truncate block"
                        >
                          {g.name}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-500 truncate">{g.description || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                          <Users className="w-4 h-4 text-gray-400" />
                          {g.member_count ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 overflow-hidden">
                          {(g.rights ?? []).slice(0, 2).map((r) => (
                            <span
                              key={r.id}
                              className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full whitespace-nowrap shrink-0"
                            >
                              {r.label}
                            </span>
                          ))}
                          {(g.rights?.length ?? 0) > 2 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full whitespace-nowrap shrink-0">
                              +{(g.rights?.length ?? 0) - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs rounded-full font-medium whitespace-nowrap ${g.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                        >
                          {g.is_active ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setDetailGroup(g)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title={t('adminGroups.manageGroup')}
                          >
                            <Users className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setFormGroup(g)}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                            title={t('common.edit')}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(g)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title={t('common.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {formGroup !== null && (
        <GroupFormModal group={formGroup} onSave={handleSave} onClose={() => setFormGroup(null)} />
      )}
      {detailGroup && (
        <GroupDetailPanel
          group={detailGroup}
          allUsers={allUsers}
          onClose={() => setDetailGroup(null)}
          onUpdated={load}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          message={t('adminGroups.confirmDelete', { name: deleteTarget.name })}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  )
}
